import { useEffect, useState, useCallback } from "react";
import { ShieldCheck, ShieldOff, Copy } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";

const inp = "w-full bg-[#0A0A0C] border border-[#27272A] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#E8552A] font-mono tracking-widest";

export function TwoFactorCard({ hasPassword = true }) {
  const [st, setSt] = useState(null);
  const [setup, setSetup] = useState(null);
  const [code, setCode] = useState("");
  const [pw, setPw] = useState("");
  const [recovery, setRecovery] = useState(null);
  const load = useCallback(() => api.get("/auth/2fa/status").then((r) => setSt(r.data)).catch(() => setSt({ enabled: false })), []);
  useEffect(() => { load(); }, [load]);
  const begin = async () => { try { const { data } = await api.post("/auth/2fa/setup"); setSetup(data); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const enable = async () => {
    try { const { data } = await api.post("/auth/2fa/enable", { code }); setRecovery(data.recovery_codes); setSetup(null); setCode(""); load(); toast.success("Two-factor enabled"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const disable = async () => {
    try { await api.post("/auth/2fa/disable", { code, password: pw }); setCode(""); setPw(""); load(); toast.success("Two-factor disabled"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  if (!st) return null;
  return (
    <div data-testid="twofa-card" className="space-y-3">
      <div className="flex items-center justify-between text-sm">
        <span className="flex items-center gap-2">{st.enabled ? <ShieldCheck size={16} className="text-[#10B981]" /> : <ShieldOff size={16} className="text-[#71717A]" />} Two-factor authentication</span>
        <span data-testid="twofa-status" className={`mono-label ${st.enabled ? "text-[#10B981]" : "text-[#71717A]"}`}>{st.enabled ? `ON · ${st.recovery_left} RECOVERY CODES` : "OFF"}</span>
      </div>
      {!st.enabled && !setup && !recovery && <button data-testid="twofa-begin" onClick={begin} className="copper-btn rounded-lg px-4 py-2 mono-label">ENABLE AUTHENTICATOR APP</button>}
      {setup && (
        <div className="space-y-2">
          <p className="text-xs text-[#A1A1AA]">Scan with Google Authenticator, Authy or 1Password, then enter the 6-digit code.</p>
          <img data-testid="twofa-qr" src={setup.qr_png} alt="2FA QR" className="w-44 h-44 rounded-lg border border-[#27272A] bg-white" />
          <p className="mono-label text-[#71717A] break-all">MANUAL KEY: {setup.secret}</p>
          <div className="flex gap-2"><input data-testid="twofa-code" value={code} onChange={(e) => setCode(e.target.value)} placeholder="123 456" className={inp} /><button data-testid="twofa-confirm" onClick={enable} className="copper-btn rounded-lg px-4 mono-label">CONFIRM</button></div>
        </div>
      )}
      {recovery && (
        <div data-testid="twofa-recovery" className="rounded-lg border border-[#FF9F1C]/40 p-3">
          <p className="mono-label text-[#FF9F1C]">SAVE THESE RECOVERY CODES — SHOWN ONCE</p>
          <div className="grid grid-cols-2 gap-1 mt-2 font-mono text-xs">{recovery.map((c) => <span key={c}>{c}</span>)}</div>
          <button onClick={() => { navigator.clipboard?.writeText(recovery.join("\n")); toast.success("Copied"); }} className="mono-label text-[#E8552A] mt-2 flex items-center gap-1"><Copy size={12} /> COPY</button>
          <button onClick={() => setRecovery(null)} className="mono-label text-[#71717A] mt-2 block">DONE</button>
        </div>
      )}
      {st.enabled && !recovery && (
        <div className="space-y-2">
          <p className="text-xs text-[#A1A1AA]">To turn off, confirm {hasPassword ? "your password and " : ""}a current authenticator or recovery code.</p>
          {hasPassword && <input data-testid="twofa-disable-pw" type="password" value={pw} onChange={(e) => setPw(e.target.value)} placeholder="Password" className={inp} />}
          <div className="flex gap-2"><input data-testid="twofa-disable-code" value={code} onChange={(e) => setCode(e.target.value)} placeholder="Code" className={inp} /><button data-testid="twofa-disable" onClick={disable} className="border border-[#ef4444]/50 text-[#ef4444] rounded-lg px-4 mono-label">DISABLE</button></div>
        </div>
      )}
    </div>
  );
}
